import { useEffect, useState } from "react";
import axios from "axios";
import DynamicTable from "../../../Components/DynamicTable";
import AddProduct from "./AddProduct";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { useUser } from "../../../UserContext";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";

// Define interfaces
interface Product {
  id: number;
  product_id: number;
  brand_id: number;
  product: string;
  category_id: string;
  category: string;
  msrp: number;
  case_pack: number;
  metadata: { abv: number };
  format: string;
  size: { value: number; unit_of_measure: string };
  brand_name: string;
  unit_of_sale: string;
  [key: string]: any; // Add index signature
}

interface Category {
  value: string;
  label: string;
}

interface Format {
  id: number;
  item: string;
}

interface UnitOfSale {
  id: number;
  item: string;
}

interface UnitOfMeasure {
  id: number;
  label: string;
  value: string;
}

interface Column {
  header: string;
  dataIndex?: keyof Product;
  key: string;
  render?: (_: any, record: Product) => React.ReactNode;
  align?: "center" | "left" | "right" | undefined;
}

// Add this helper function at the top of the file
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

// Separate component for the edit form
const EditForm = ({
  editData,
  handleEditChange,
  categories,
  formats,
  unitsOfSale,
  unitsOfMeasure,
}: {
  editData: Product;
  handleEditChange: (field: string, value: any) => void;
  categories: Category[];
  formats: Format[];
  unitsOfSale: UnitOfSale[];
  unitsOfMeasure: UnitOfMeasure[];
}) => (
  <Stack spacing={0.5} sx={{ p: 2 }}>
    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Basic Information</Typography>
        <TextField
          label="Product Name"
          size="small"
          value={editData.product}
          onChange={(e) => handleEditChange("product", e.target.value)}
          fullWidth
        />
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option.label}
          value={{
            value: editData.category_id,
            label: editData.category,
          }}
          onChange={(_, newValue) => {
            handleEditChange("category_id", newValue?.value || "");
            handleEditChange("category", newValue?.label || "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Category" size="small" />
          )}
        />
      </Stack>
    </Paper>

    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Product Details</Typography>
        <TextField
          label="MSRP"
          type="number"
          size="small"
          value={editData.msrp}
          onChange={(e) => handleEditChange("msrp", Number(e.target.value))}
          fullWidth
          InputProps={{
            startAdornment: <span style={{ marginRight: 8 }}>$</span>,
          }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            sx={{ flex: 1 }}
            label="Size Value"
            type="number"
            size="small"
            value={editData.size.value}
            onChange={(e) =>
              handleEditChange("size.value", Number(e.target.value))
            }
          />
          <TextField
            select
            sx={{ width: "120px" }}
            label="Unit"
            size="small"
            value={editData.size.unit_of_measure}
            onChange={(e) =>
              handleEditChange("size.unit_of_measure", e.target.value)
            }
          >
            {unitsOfMeasure.map((uom) => (
              <MenuItem key={uom.id} value={uom.value}>
                {uom.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField
          label="Pack"
          type="number"
          size="small"
          value={editData.case_pack}
          onChange={(e) =>
            handleEditChange("case_pack", Number(e.target.value))
          }
          fullWidth
        />
        <TextField
          label="ABV"
          type="number"
          size="small"
          value={editData.metadata.abv}
          onChange={(e) =>
            handleEditChange("metadata.abv", Number(e.target.value))
          }
          fullWidth
        />
        <TextField
          select
          label="Format"
          size="small"
          value={editData.format}
          onChange={(e) => handleEditChange("format", e.target.value)}
          fullWidth
        >
          {formats.map((format) => (
            <MenuItem key={format.id} value={format.item}>
              {format.item}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Unit of Sale"
          size="small"
          value={editData.unit_of_sale}
          onChange={(e) => handleEditChange("unit_of_sale", e.target.value)}
          fullWidth
        >
          {unitsOfSale.map((uos) => (
            <MenuItem key={uos.id} value={uos.item}>
              {uos.item}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Paper>
  </Stack>
);

interface ProductsProps {
  readOnly?: boolean;
  productsData?: Product[];
}

const Products: React.FC<ProductsProps> = ({
  readOnly = false,
  productsData,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { selectedBrandId } = useUser();
  const [formats, setFormats] = useState<Format[]>([]);
  const [unitsOfSale, setUnitsOfSale] = useState<UnitOfSale[]>([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<UnitOfMeasure[]>([]);

  const productColumns: Column[] = [
    { header: "Product", dataIndex: "product", key: "product" },
    { header: "Category", dataIndex: "category", key: "category" },
    {
      header: "MSRP",
      align: "center",
      key: "msrp",
      render: (_, record: Product) => formatCurrency(record.msrp),
    },
    {
      header: "Size",
      key: "size",
      align: "center",
      render: (_, record: Product) => {
        if (!record?.size) return "";
        return `${record.size.value} ${record.size.unit_of_measure}`;
      },
    },
    {
      header: "Pack",
      dataIndex: "case_pack",
      key: "case_pack",
      align: "center",
    },
    {
      header: "ABV",
      key: "abv",
      align: "center",
      render: (_, record: Product) => {
        if (!record?.metadata?.abv) return "";
        return `${record.metadata.abv}%`;
      },
    },
    {
      header: "Format",
      dataIndex: "format",
      align: "center",
      key: "format",
    },
    {
      header: "Unit of Sale",
      dataIndex: "unit_of_sale",
      align: "center",
      key: "unit_of_sale",
    },
  ];

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setEditingProduct(null);
  };

  const handleEditChange = (field: string, value: any) => {
    if (editingProduct) {
      // Handle nested properties
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        setEditingProduct({
          ...editingProduct,
          [parent]: {
            ...editingProduct[parent],
            [child]: value,
          },
        });
      } else {
        setEditingProduct({ ...editingProduct, [field]: value });
      }
    }
  };

  const handleSave = async () => {
    if (!editingProduct) return;

    // Create a copy with category_id as integer
    const productToUpdate = {
      ...editingProduct,
      category_id: parseInt(editingProduct.category_id),
    };

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/brands/products/${editingProduct.id}`,
        productToUpdate
      );

      setProducts(
        products.map((product) =>
          product.id === editingProduct.id ? editingProduct : product
        )
      );

      setSnackbarOpen(true);
      handleCloseSidebar();
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleDelete = async () => {
    if (!editingProduct) return;

    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/brands/products/${editingProduct.id}`
      );
      setProducts(
        products.filter((product) => product.id !== editingProduct.id)
      );
      setDeleteConfirmOpen(false);
      handleCloseSidebar();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  // Update the mapProductData function
  const mapProductData = (item: any) => {
    // Parse JSONB fields if they're strings
    let sizeData;
    try {
      sizeData =
        typeof item.size === "string" ? JSON.parse(item.size) : item.size;
    } catch (error) {
      console.error("Error parsing size data:", error);
      sizeData = { value: 0, unit_of_measure: "ML" };
    }

    const metadataData = { abv: Number(item.abv) };

    return {
      id: item.product_id,
      product_id: item.product_id,
      brand_id: item.brand_id,
      product: item.product,
      category_id: item.category_id.toString(),
      category: item.category,
      msrp: Number(item.msrp),
      case_pack: item.case_pack,
      metadata: metadataData,
      format: item.format,
      size: sizeData || { value: 0, unit_of_measure: "ML" },
      brand_name: item.brand_name,
      unit_of_sale: item.unit_of_sale,
    };
  };

  useEffect(() => {
    if (productsData) {
      // Map the incoming products data to match our format
      const mappedProducts = productsData.map(mapProductData);
      setProducts(mappedProducts);
    } else if (!readOnly && selectedBrandId) {
      const fetchProducts = async () => {
        try {
          const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/brands/products/${selectedBrandId}`
          );
          const mappedProducts = response.data.map(mapProductData);
          setProducts(mappedProducts);
        } catch (error) {
          console.error("Error fetching products:", error);
        }
      };
      fetchProducts();
    }

    // Only fetch categories and util data if not in readOnly mode
    if (!readOnly) {
      const fetchData = async () => {
        try {
          const [categoriesRes, utilDataRes] = await Promise.all([
            axios.get(
              `${import.meta.env.VITE_API_URL}/brands/product-categories`
            ),
            axios.get(`${import.meta.env.VITE_API_URL}/brands/util-data`),
          ]);

          // Ensure categories is an array
          setCategories(
            Array.isArray(categoriesRes.data) ? categoriesRes.data : []
          );

          // Check if utilDataRes.data is an array
          if (Array.isArray(utilDataRes.data)) {
            const utilData = utilDataRes.data;
            setFormats(utilData.find((d: any) => d.id === 4)?.data || []);
            setUnitsOfSale(utilData.find((d: any) => d.id === 5)?.data || []);
            setUnitsOfMeasure(
              utilData.find((d: any) => d.id === 6)?.data || []
            );
          } else {
            console.error("Unexpected utilData format:", utilDataRes.data);
            setFormats([]);
            setUnitsOfSale([]);
            setUnitsOfMeasure([]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setCategories([]);
          setFormats([]);
          setUnitsOfSale([]);
          setUnitsOfMeasure([]);
        }
      };
      fetchData();
    }
  }, [selectedBrandId, productsData, readOnly]);

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <AddProduct
            categories={categories}
            formats={formats}
            unitsOfSale={unitsOfSale}
            unitsOfMeasure={unitsOfMeasure}
            brandId={selectedBrandId || 0}
            onClose={() => {
              /* handle close */
            }}
            onSave={async () => {
              if (!readOnly && selectedBrandId) {
                const response = await axios.get(
                  `${
                    import.meta.env.VITE_API_URL
                  }/brands/products/${selectedBrandId}`
                );
                const mappedProducts = response.data.map(mapProductData);
                setProducts(mappedProducts);
              }
            }}
          />
        </Box>
      )}

      <DynamicTable
        data={products}
        columns={productColumns}
        onRowClick={readOnly ? undefined : handleEdit}
      />

      {!readOnly && (
        <SlidingSidebar
          open={sidebarOpen}
          onClose={handleCloseSidebar}
          width="500px"
        >
          {editingProduct && (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
                <EditForm
                  editData={editingProduct}
                  handleEditChange={handleEditChange}
                  categories={categories}
                  formats={formats}
                  unitsOfSale={unitsOfSale}
                  unitsOfMeasure={unitsOfMeasure}
                />
              </Box>
              <ButtonControls>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  color="primary"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => setDeleteConfirmOpen(true)}
                  variant="outlined"
                  color="error"
                >
                  Delete Product
                </Button>
                <Button onClick={handleCloseSidebar} variant="outlined">
                  Cancel
                </Button>
              </ButtonControls>
            </Box>
          )}
        </SlidingSidebar>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this product? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
